import { useEffect } from 'react';
import { Box, Container, Typography, Chip, Stack, Divider, Avatar, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
// ⚡ Path-based imports (6× faster than barrel imports)
import DateIcon from '@mui/icons-material/CalendarMonth';
import ReadTimeIcon from '@mui/icons-material/Schedule';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MDXProvider } from '@/components/MDXProvider';
import { analytics } from '@/lib/analytics';
import { useScrollDepth } from '@/hooks/useScrollDepth';
import { formatArticleDate, hasEdits, presentedDate, type ArticleEdit } from '@/lib/articleEdits';

/**
 * Layout wrapper for MDX article pages
 * Provides consistent styling and metadata display
 */
interface ArticleLayoutProps {
  title: string;
  description?: string;
  date?: string;
  edits?: ArticleEdit[];
  slug?: string;
  readTime?: string;
  tags?: string[];
  author?: string;
  authorImage?: string;
  headerImage?: string;
  children: React.ReactNode;
}

export function ArticleLayout({
  title,
  description,
  date,
  edits,
  slug: slugProp,
  readTime,
  tags,
  author,
  authorImage,
  headerImage,
  children,
}: ArticleLayoutProps) {
  const slug = slugProp ?? title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const shown = presentedDate(date, edits);
  const edited = hasEdits(edits);

  useScrollDepth(slug);

  useEffect(() => {
    analytics.articleViewed(slug, 'blog', title, readTime ?? '');
  }, [slug, title, readTime]);

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      {/* Header Image */}
      {headerImage && (
        <Box
          component="img"
          src={headerImage}
          alt={title}
          sx={{
            width: '100%',
            height: { xs: 200, sm: 300, md: 400 },
            objectFit: 'cover',
            borderRadius: 2,
            mb: 4,
            boxShadow: 2,
          }}
        />
      )}

      {/* Article Header */}
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h3"
          component="h1"
          fontWeight="bold"
          gutterBottom
          sx={{
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
          }}
        >
          {title}
        </Typography>

        {description && (
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              mt: 2,
              mb: 3,
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              lineHeight: 1.5,
              fontWeight: 400,
            }}
          >
            {description}
          </Typography>
        )}

        {/* Metadata */}
        <Stack
          direction="row"
          spacing={3}
          alignItems="center"
          sx={{ mb: 2.5 }}
          flexWrap="wrap"
        >
          {author && (
            <Stack direction="row" spacing={1} alignItems="center">
              {authorImage ? (
                <Avatar
                  src={authorImage}
                  alt={author}
                  sx={{ width: 32, height: 32 }}
                />
              ) : (
                <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              )}
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {author}
              </Typography>
            </Stack>
          )}

          {shown && (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <DateIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {formatArticleDate(shown)}
              </Typography>
            </Stack>
          )}

          {readTime && (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <ReadTimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {readTime}
              </Typography>
            </Stack>
          )}
        </Stack>

        {edited && date && (
          <Box sx={{ mb: 2.5, maxWidth: 560 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Originally posted {formatArticleDate(date)}
            </Typography>
            <Accordion
              disableGutters
              elevation={0}
              onChange={(_, expanded) => {
                if (expanded) analytics.articleEditsOpened(slug, edits!.length);
              }}
              sx={{
                bgcolor: 'transparent',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44 }}>
                <Typography variant="body2" fontWeight={600}>
                  Edits
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Stack spacing={1.25} component="ol" sx={{ m: 0, pl: 2.5 }}>
                  {edits!.map((edit) => (
                    <Box component="li" key={`${edit.date}-${edit.note}`}>
                      <Typography variant="body2" fontWeight={600}>
                        {formatArticleDate(edit.date)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {edit.note}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        {/* Tags */}
        {tags && tags.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{
                  fontWeight: 500,
                  borderRadius: 1,
                }}
              />
            ))}
          </Stack>
        )}

        <Divider sx={{ mt: 4, mb: 1 }} />
      </Box>

      {/* Article Content */}
      <MDXProvider>
        <Box
          className="prose prose-slate max-w-none"
          sx={{
            // Better spacing and readability
            '& > *': {
              maxWidth: '100%',
            },
            // Math styling
            '& .math': {
              overflowX: 'auto',
              overflowY: 'hidden',
            },
            '& .katex-display': {
              overflowX: 'auto',
              overflowY: 'hidden',
            },
            // Code block styling
            '& pre': {
              borderRadius: 2,
              my: 3,
            },
            // Image styling
            '& img': {
              borderRadius: 2,
              boxShadow: 1,
            },
            // Blockquote spacing
            '& .MuiAlert-root': {
              my: 3,
            },
            // Table container - full width breaking out of prose constraints
            '& .MuiTableContainer-root': {
              my: 3,
              width: '100% !important',
              maxWidth: '100% !important',
              marginLeft: '0 !important',
              marginRight: '0 !important',
            },
            // Better paragraph spacing
            '& p': {
              mb: 2.5,
              lineHeight: 1.8,
            },
            // Heading spacing
            '& h2': {
              mt: 5,
              mb: 2.5,
            },
            '& h3': {
              mt: 4,
              mb: 2,
            },
            '& h4': {
              mt: 3,
              mb: 1.5,
            },
            // List spacing
            '& ul, & ol': {
              mb: 2.5,
            },
            '& li': {
              mb: 0.75,
            },
          }}
        >
          {children}
        </Box>
      </MDXProvider>
    </Container>
  );
}
